import {standardSchemaResolver} from '@hookform/resolvers/standard-schema';
import {type JSX, createContext, useContext} from 'react';
import {FieldValues, FormProvider, SubmitErrorHandler, SubmitHandler, UseFormProps, useForm} from 'react-hook-form';
import {ZodType} from 'zod';
import {PropsWithRequiredChildren} from '../react/types';
import {logger} from '#common/logging/logger';

export interface FormProps<TFieldValues extends FieldValues> extends PropsWithRequiredChildren<
  UseFormProps<TFieldValues>
> {
  onSubmit: SubmitHandler<TFieldValues>;
  onInvalid?: SubmitErrorHandler<TFieldValues>;
  schema: ZodType<TFieldValues, TFieldValues>;
}

interface FormHandlers {
  doSubmit: () => void | Promise<void>;
}

/* v8 ignore next -- @preserve */
const FormHandlersContext = createContext<FormHandlers>({
  doSubmit: () => {
    // Defaults to no-op; will be initialized in FormHandlersProvider
  },
});

export function useFormHandlers(): FormHandlers {
  return useContext(FormHandlersContext);
}

function FormHandlersProvider({children, ...handlers}: PropsWithRequiredChildren<FormHandlers>): JSX.Element {
  return <FormHandlersContext.Provider value={handlers}>{children}</FormHandlersContext.Provider>;
}

export function Form<TFieldValues extends FieldValues>({
  children,
  delayError = 1000,
  mode = 'onChange',
  onInvalid: onInvalidProp,
  onSubmit,
  reValidateMode = 'onChange',
  schema,
  ...props
}: FormProps<TFieldValues>): JSX.Element {
  const formFunctions = useForm<TFieldValues>({
    delayError,
    mode,
    reValidateMode,
    resolver: standardSchemaResolver(schema),
    ...props,
  });
  const {handleSubmit} = formFunctions;

  const onInvalid: SubmitErrorHandler<TFieldValues> = (errors, event) => {
    logger.error('Form validation failed:', errors);
    onInvalidProp?.(errors, event);
  };

  const doSubmit = handleSubmit(onSubmit, onInvalid);

  return (
    <FormProvider {...formFunctions}>
      <FormHandlersProvider doSubmit={doSubmit}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void doSubmit(e);
          }}
        >
          {children}
        </form>
      </FormHandlersProvider>
    </FormProvider>
  );
}
